import React, { useEffect, useMemo, useState } from 'react'
import { Col, Container, Placeholder, Row, Spinner } from 'react-bootstrap'
import styled from 'styled-components'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import {
  SubscriptionChangeStatus,
  useCancelDeltaTrialMutation,
  useChangePlanMutation,
  useGetBillingCustomerQuery,
  useGetDeltaTrialsQuery,
  useGetPlansQuery,
} from '../../store/plan/plan'
import { Button } from '../../components/Button/Button'
import { TwoWaySwitch } from '../../components/TwoWaySwitch/TwoWaySwitch'
import { CurrentPlan } from './CurrentPlan/CurrentPlan'
import classNames from 'classnames'
import { PlanCompareTable } from './PlanCompareTable/PlanCompareTable'
import { UpgradeModal, UpgradeModalType } from './UpgradeModal/UpgradeModal'
import { Plan, PricePeriod } from '../../models/Plan'
import { CurrencyDiv } from './CurrencyDiv'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUsedSeats } from '../../common/useUsedSeats'
import { Modal } from '../../components/Modal/Modal'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { mapEnterpriseBillingOptions } from './utils'
import { DELTA_TRIAL_FEATURES } from './DeltaTrial/DeltaTrialFeatures'
import './Plan.scss'

const YearlyPromotionBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.25rem;
  border-radius: 0.125rem;
  background: var(--zocks-primary-light, #ebe5fc);
`

export const PlanTab: React.FC = () => {
  const navigate = useNavigate()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const [period, setPeriod] = useState<PricePeriod>('year')
  const { data: globalPlans, isFetching: plansLoading } = useGetPlansQuery(undefined)
  const [pollingInterval, setPollingInterval] = useState(0)
  const { data: customer, isFetching: customerLoading } = useGetBillingCustomerQuery(
    { accountId },
    { refetchOnMountOrArgChange: true, pollingInterval: pollingInterval },
  )
  const { data: deltaTrials } = useGetDeltaTrialsQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [triggerCancelDeltaTrial, { isLoading: cancelDeltaTrialLoading }] = useCancelDeltaTrialMutation()

  const [enterpriseBillingSetupLoading, setEnterpriseBillingSetupLoading] = useState<boolean>(false)
  const { data: account, isLoading: accountLoading } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const hideBillingInfo = !!customer?.data.enterpriseSubscription?.enterpriseBillingConfig?.package.hideBillingInfo

  useEffect(() => {
    if (!account || !customer) {
      return
    }

    const { metadata: { viaEnterpriseFlow, missingEnterpriseBillingConfig } = {} } = account.data
    if (viaEnterpriseFlow) {
      const hasEnterpriseSubscription = !!customer.data.enterpriseSubscription
      const hasAggregateSubscription = customer.data.enterpriseSubscription?.enterpriseBillingConfig?.package.type === 'aggregate'
      const hasSubscription = !!customer.data.subscription
      setEnterpriseBillingSetupLoading(!missingEnterpriseBillingConfig && (!hasEnterpriseSubscription || (hasAggregateSubscription && !hasSubscription)))
    }
  }, [account, customer])

  useEffect(() => {
    setPollingInterval(enterpriseBillingSetupLoading ? 5000 : 0)
  }, [enterpriseBillingSetupLoading])

  const plans = useMemo((): Plan[] => {
    if (!globalPlans || !customer || enterpriseBillingSetupLoading) {
      return []
    }
    if (!customer.data.enterpriseSubscription) {
      return globalPlans.data
    }
    const { package: enterpriseBillingPackage } = customer.data.enterpriseSubscription.enterpriseBillingConfig
    return mapEnterpriseBillingOptions(enterpriseBillingPackage, enterpriseBillingPackage.billingOptions, globalPlans.data)
  }, [globalPlans, customer, enterpriseBillingSetupLoading])

  const isEnterpriseAggregate = customer?.data.enterpriseSubscription?.enterpriseBillingConfig?.package.type === 'aggregate'
  const isEnterpriseSelfPaid = customer?.data.enterpriseSubscription?.enterpriseBillingConfig?.package.type === 'selfPaid'

  const { getQuotaValue } = useZocksQuota()
  const freeAssistantSeatMultiplier = Number(getQuotaValue('identity.sidecarSeats.value'))
  const [changePlan, { isLoading: isUpdating }] = useChangePlanMutation()
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [selectedFreeTrial, setSelectedFreeTrial] = useState<boolean>(false)
  const [selectedUpgradeModalType, setSelectedUpgradeModalType] = useState<UpgradeModalType>('seat')
  const [searchParams, setSearchParams] = useSearchParams()
  const periodFromQuery = searchParams.get('period')
  const usedSeats = useUsedSeats()
  const [showDowngradeWarningModal, setShowDowngradeWarningModal] = useState(false)
  const [switchFreeTrialLoading, setSwitchFreeTrialLoading] = useState<boolean>(false)
  const canStartFreeTrial = !customer?.data.internalMetadata?.cannotStartFreeTrial

  const onHideUpgradeModal = () => {
    setShowUpgradeModal(false)
  }

  const onUpgradeSave = () => {
    setShowUpgradeModal(false)
    setPollingInterval(2000)
  }

  useEffect(() => {
    if (periodFromQuery) {
      setPeriod(periodFromQuery as PricePeriod)
    }
  }, [periodFromQuery])

  const handleUpgrade = tracked(
    (type: UpgradeModalType, plan: Plan | null, options: { freeTrial: boolean } = { freeTrial: false }) => {
      const { freeTrial } = options
      setSelectedUpgradeModalType(type)
      setSelectedFreeTrial(freeTrial)
      if (customerPlan && plan && plan.tier < customerPlan.tier && customerPlan.prices.some((p) => p.period === period)) {
        setSelectedPlan(customerPlan)
      } else {
        setSelectedPlan(plan)
      }
      if (customer?.data.subscription && (type === 'seat' || type === 'assistant')) {
        setPeriod(customer.data.subscription.period)
      }
      setShowUpgradeModal(true)
    },
    {},
    {
      eventNameExtractor: (type: UpgradeModalType, plan: Plan | null) => {
        return type === 'seat'
          ? TrackEvent.PLAN_UPGRADE_SEAT_OPENED
          : type === 'assistant'
            ? TrackEvent.PLAN_UPGRADE_ASSISTANT_SEAT_OPENED
            : customerPlan && plan!.tier < customerPlan.tier
              ? TrackEvent.PLAN_MANAGE_PLAN_OPENED
              : TrackEvent.PLAN_UPGRADE_PLAN_OPENED
      },
      eventDetailsExtractor: (type: UpgradeModalType, plan: Plan | null) => {
        return plan
          ? {
              plan: plan.id,
              period: period,
            }
          : {}
      },
    },
  )

  const handleStartDeltaTrial = async (planId: string) => {
    const feature = Object.values(DELTA_TRIAL_FEATURES).find((f) => f.planId === planId)
    if (!feature) {
      return
    }
    navigate(`/account/plan/free-trial/${feature.id}`)
  }

  const _handleCancelDeltaTrial = async (planId: string) => {
    try {
      await triggerCancelDeltaTrial({ accountId, planId }).unwrap()
      setPollingInterval(3000)
    } catch (e) {
      console.error(e)
    }
  }

  const handleCancelDeltaTrial = tracked(
    _handleCancelDeltaTrial,
    {
      eventName: TrackEvent.PLAN_DELTA_TRAL_CANCELED,
    },
    {
      eventDetailsExtractor: (planId: string) => {
        return { planId }
      },
    },
  )

  const selfPaidPlanHasMultiplePaymentTypes = (plan: Plan) => {
    const enterpriseBillingPackage = customer?.data.enterpriseSubscription?.enterpriseBillingConfig?.package
    if (enterpriseBillingPackage?.type !== 'selfPaid') {
      return false
    }
    const billingOption = enterpriseBillingPackage.billingOptions.find((o) => o.planId === plan.id && o.period === period)
    if (!billingOption) {
      return false
    }
    return billingOption.paymentTypes.length > 1
  }

  const handleFreeUpgrade = (plan: Plan) => {
    setSelectedPlan(plan)
    if (customerPlan && customerPlan.tier > plan.tier) {
      setShowDowngradeWarningModal(true)
    } else {
      _handleFreeUpgrade(plan)
    }
  }

  const _handleFreeUpgrade = tracked(
    async (plan: Plan) => {
      setShowDowngradeWarningModal(false)
      if (plan.trialPeriodDays && customer) {
        try {
          setSwitchFreeTrialLoading(true)
          const response = await changePlan({
            accountId,
            payload: {
              planId: plan.id,
              period: period,
              numberOfSeats: customer.data.subscription?.numberOfSeats ?? usedSeats,
              freeTrial: true,
            },
          }).unwrap()
          if (response.data.result === SubscriptionChangeStatus.CHECKOUT_REQUIRED.valueOf()) {
            window.location.href = response.data.checkoutSessionUrl
          } else {
            setPollingInterval(2000)
          }
        } finally {
          setSwitchFreeTrialLoading(false)
        }
      }
    },
    { eventName: TrackEvent.PLAN_TRY_FOR_FREE_CLICK },
  )

  useEffect(() => {
    if (!customer?.data) {
      return
    }
    if (customer.data.subscription && !periodFromQuery) {
      setPeriod(customer.data.subscription.period)
    }
    if (customer.data.subscriptionStatus === 'subscribed') {
      setPollingInterval(0)
    }
  }, [customer, periodFromQuery])

  if (enterpriseBillingSetupLoading) {
    return (
      <div className='p-4 zocks-border-0.5 zocks-bg-color-success-background mb-5'>
        <div className='d-flex flex-column gap-2'>
          <h5>Setting up your enterprise subscription</h5>
          Please wait while setting up your enterprise subscription.
          <Spinner />
        </div>
      </div>
    )
  }

  if (plansLoading || customerLoading || !plans || !customer || accountLoading) {
    return (
      <Container>
        <Row>
          <Placeholder xs={12} sm={12} />
        </Row>
      </Container>
    )
  }

  const customerPlan = plans.find((plan) => plan.id === customer.data.subscription?.planId)
  const assistantEnabled = !!customerPlan?.additionalProducts.find((additionalProduct) => additionalProduct.type === 'assistant')

  const isCurrentPlan = (plan: Plan) => {
    return plan.id === customerPlan?.id && customer.data.subscription?.period === period
  }

  const isInFreeTrial = (plan: Plan) => {
    if (customer) {
      return plan.id === customerPlan?.id && customer.data.freeTrial && customer.data.freeTrial.startedAt && !customer.data.freeTrial.endedAt
    }
    return false
  }

  const isInDeltaTrial = (plan: Plan) => {
    if (customer?.data.deltaTrial) {
      return plan.id === customer.data.deltaTrial.planId
    }
    return false
  }

  const canStartDeltaTrial = (plan: Plan) => {
    if (!plan.deltaTrialDays) {
      return false
    }
    if (!customer?.data || !customer.data.subscription || customer.data.deltaTrial || !deltaTrials) {
      return false
    }
    if (customer.data.subscription.planId === plan.id) {
      return false
    }
    if (customer.data.freeTrial && !customer.data.freeTrial.endedAt) {
      return false
    }
    return !deltaTrials.some((trial) => trial.planId === plan.id)
  }

  const _handleOnPeriodChange = (option: PricePeriod) => {
    setPeriod(option)
    setSearchParams((params) => {
      params.set('period', option)
      return params
    })
  }

  const handleOnPeriodChange = tracked(
    (option: PricePeriod) => _handleOnPeriodChange(option),
    {
      eventName: TrackEvent.PLAN_PERIOD_CHANGE,
    },
    {
      eventDetailsExtractor: (option: PricePeriod) => {
        return {
          period: option,
        }
      },
    },
  )

  return (
    <Container fluid className='p-0'>
      <Modal
        show={showDowngradeWarningModal}
        onHide={() => {
          if (customerPlan) {
            setSelectedPlan(customerPlan)
          }
          setShowDowngradeWarningModal(false)
        }}
        header={<h4>Downgrade Notice!</h4>}
        body={
          <>
            <div className='mb-2'>Downgrading your subscription means losing access to the following features:</div>
            <ul>
              <li>Assistant seat for free</li>
              <li>User Level Intelligence customization</li>
              <li>Fine grained access control for sharing</li>
              <li>Custom data retention policy</li>
            </ul>
          </>
        }
        showFooterActions={true}
        centered={true}
        onFooterPrimaryAction={() => {
          if (selectedPlan) {
            _handleFreeUpgrade(selectedPlan)
          }
        }}
        footerPrimaryActionLoading={isUpdating}
      />

      {showUpgradeModal && (
        <UpgradeModal
          show={showUpgradeModal}
          onHide={onHideUpgradeModal}
          onSave={onUpgradeSave}
          initialType={selectedUpgradeModalType}
          period={period}
          plan={selectedPlan! || customerPlan}
          freeTrial={selectedFreeTrial}
          plans={plans}
          customer={customer.data}
        />
      )}
      {!canStartFreeTrial ? (
        <Row>
          <Col>
            <div className='zocks-bg-color-warning p-4 zocks-border-0.5 mb-3'>
              The provided payment method was already used for a free trial. Please provide another payment method, or subscribe to a plan without starting free
              trial.
            </div>
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col>
          <CurrentPlan />
        </Col>
      </Row>
      {plans && !customerLoading && (
        <>
          <Row className='mb-3'>
            <Col>
              <div className='lead-medium'>{!customer.data.subscription ? 'Choose your plan' : 'Upgrade your plan'}</div>
            </Col>
          </Row>
          <Row xs='auto' className='d-flex align-items-center justify-content-center mb-3'>
            <Col className='py-3 py-md-0' xs={{ span: 12, order: 'first' }} md={{ span: 'auto', order: 'first' }}>
              <Container className='p-0'>
                <Row className='align-items-center'>
                  <Col>
                    <TwoWaySwitch leftOption={'month'} rightOption={'year'} selectedOption={period} onChange={handleOnPeriodChange} />
                  </Col>
                  <Col>
                    <div className='d-flex align-items-center justify-content-center gap-1 paragraph-small text-nowrap'>
                      Save <YearlyPromotionBox className='lead-extra-small'>2 months</YearlyPromotionBox> with annual plans over monthly
                    </div>
                  </Col>
                </Row>
              </Container>
            </Col>
            <Col xs={12} md='auto'>
              <div className='number-of-seats-container'>
                <div className='d-flex gap-3 justify-content-between align-items-center'>
                  <div className='d-flex gap-2 align-items-center'>
                    <div className='lead-small'>Number of seats</div>
                    <div className='number-of-seats lead-small'>{customer.data.subscription?.numberOfSeats ?? usedSeats}</div>
                  </div>
                  <Button variant='link' className='button-link-small' disabled={!customer.data.subscription} onClick={() => handleUpgrade('seat', null)}>
                    Upgrade
                  </Button>
                  {assistantEnabled && (
                    <>
                      <div className='d-flex gap-2 align-items-center'>
                        <div className='lead-small'>Assistant seats</div>
                        <div className='number-of-seats lead-small'>
                          {freeAssistantSeatMultiplier * (customer.data.subscription?.numberOfSeats ?? usedSeats) +
                            (customer.data.subscription?.additionalProducts['assistant']?.quantity ?? 0)}
                        </div>
                      </div>
                      <Button
                        variant='link'
                        className='button-link-small'
                        disabled={!customer.data.subscription}
                        onClick={() => handleUpgrade('assistant', null)}
                      >
                        Upgrade
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
          <Row className='d-flex justify-content-center gy-3'>
            {pollingInterval > 0 ? (
              <Placeholder xs={12} size='lg' />
            ) : (
              plans
                .filter((plan) => plan.type !== 'free' && plan.type !== 'enterprise')
                .sort((a, b) => a.tier - b.tier)
                .map((plan) => {
                  if (plan.type === 'free' || plan.type === 'payAsYouGo') {
                    return null
                  }
                  const periodPrice = plan.prices.find((price) => price.period === period)
                  if (!periodPrice) {
                    return null
                  }
                  const monthPrice = plan.prices.find((price) => price.period === 'month')
                  return (
                    <Col key={`${plan.id}-col`}>
                      <PlanCard
                        className={classNames({
                          'plan-card__current-plan': isCurrentPlan(plan),
                          'plan-card__current-plan--free-trial': (isInFreeTrial(plan) || isInDeltaTrial(plan)) && customer.data.subscription?.period === period,
                        })}
                      >
                        <div className='d-flex'>
                          <div className={classNames('plan-card__current-plan-tag lead-small', { 'opacity-0': !isCurrentPlan(plan) && !isInDeltaTrial(plan) })}>
                            {isInFreeTrial(plan) || isInDeltaTrial(plan) ? (
                              <div className='plan-card_current-plan-tag--free-trial'>Free Trial Ongoing</div>
                            ) : (
                              'Current plan'
                            )}
                          </div>
                        </div>
                        <div className='d-flex flex-column zocks-gap-3 justify-content-between h-100'>
                          <div className='d-flex flex-column zocks-gap-0.75'>
                            <h3 className='pricing-table__header__plan-name'>{plan.name}</h3>
                            <div className={classNames('paragraph-medium zocks-text-gray-600', { 'opacity-0': !plan.description })}>
                              {plan.description || '-'}
                            </div>
                          </div>
                          <div className='d-flex flex-column justify-content-center align-self-stretch gap-3'>
                            {!hideBillingInfo ? (
                              <div className='d-flex flex-column zocks-gap-0.125'>
                                <div className='d-flex flex-column gap-1'>
                                  <div className='paragraph-medium'>{periodPrice ? `Price monthly / user` : null}</div>
                                  {period === 'year' && monthPrice && periodPrice ? (
                                    <YearlyPromotionBox className='lead-extra-small align-self-start'>save 2 months over monthly plans</YearlyPromotionBox>
                                  ) : null}
                                </div>
                                <div className='d-flex gap-1'>
                                  <h1>
                                    {periodPrice?.billingScheme === 'perUnit' ? (
                                      <>
                                        <CurrencyDiv>$</CurrencyDiv>
                                        {period === 'month' ? periodPrice.amount / 100 : Math.ceil(periodPrice.amount / 100 / 12)}
                                      </>
                                    ) : (
                                      'Let’s talk'
                                    )}
                                  </h1>
                                  {!isEnterpriseAggregate && period === 'year' && periodPrice?.billingScheme === 'perUnit' ? (
                                    <div className='pb-1 paragraph-small align-self-end'>${periodPrice.amount / 100} due today</div>
                                  ) : null}
                                </div>
                              </div>
                            ) : null}
                            {!customer.data.freeTrial && plan.trialPeriodDays ? (
                              <>
                                <Button
                                  variant='primary'
                                  disabled={isCurrentPlan(plan)}
                                  onClick={
                                    isEnterpriseSelfPaid && selfPaidPlanHasMultiplePaymentTypes(plan)
                                      ? () => handleUpgrade('plan', plan, { freeTrial: true })
                                      : () => handleFreeUpgrade(plan)
                                  }
                                >
                                  Start {plan.trialPeriodDays}-day free trial
                                </Button>
                                <Button
                                  variant='link'
                                  className='py-2'
                                  disabled={isCurrentPlan(plan) && !isInFreeTrial(plan)}
                                  onClick={() => handleUpgrade('plan', plan)}
                                >
                                  Subscribe to plan
                                </Button>
                              </>
                            ) : !customerPlan && isEnterpriseSelfPaid ? (
                              <Button variant='primary' onClick={() => handleUpgrade('plan', plan)}>
                                Subscribe to plan
                              </Button>
                            ) : !customerPlan || (customer.data.freeTrial && customer.data.freeTrial.endedAt && plan.tier < customerPlan.tier) ? (
                              <Button
                                variant='link'
                                className='py-2'
                                disabled={isCurrentPlan(plan) && !isInFreeTrial(plan)}
                                onClick={() => handleUpgrade('plan', plan)}
                              >
                                Manage plan
                              </Button>
                            ) : !isCurrentPlan(plan) && customer.data.freeTrial && plan.trialPeriodDays && !customer.data.freeTrial.endedAt ? (
                              <Button variant='primary' onClick={() => handleFreeUpgrade(plan)} loading={switchFreeTrialLoading}>
                                Switch free trial
                              </Button>
                            ) : isInFreeTrial(plan) ? (
                              <div className='d-flex align-items-center justify-content-center flex-grow-1 py-2 paragraph-large fw-bold'>Your current plan</div>
                            ) : isInDeltaTrial(plan) ? (
                              <Button
                                variant='link'
                                className='py-2'
                                onClick={() => handleCancelDeltaTrial(plan.id)}
                                loading={!!pollingInterval || cancelDeltaTrialLoading}
                              >
                                Cancel Trial
                              </Button>
                            ) : canStartDeltaTrial(plan) ? (
                              <Button variant='primary' onClick={() => handleStartDeltaTrial(plan.id)} loading={!!pollingInterval || cancelDeltaTrialLoading}>
                                Start Free Trial
                              </Button>
                            ) : (
                              <Button
                                variant={customerPlan && customer.data.freeTrial && plan.tier > customerPlan.tier ? 'primary' : 'primaryGhost'}
                                disabled={isCurrentPlan(plan) && !isInFreeTrial(plan)}
                                onClick={() => handleUpgrade('plan', plan)}
                              >
                                Upgrade
                              </Button>
                            )}
                          </div>
                        </div>
                      </PlanCard>
                    </Col>
                  )
                })
            )}
          </Row>
          {!customer?.data.enterpriseSubscription ? <EnterprisePlanRow /> : null}
          {plans.length > 1 ? (
            <Row className='zocks-mt-3.5'>
              <Col>
                <div className='lead-medium zocks-mb-1.5'>Compare all plans</div>
                <PlanCompareTable plans={plans} period={period} />
              </Col>
            </Row>
          ) : null}
        </>
      )}
    </Container>
  )
}

const PlanCard = styled.div`
  display: flex;
  padding: 1rem;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  border: 1px solid var(--zocks-gray-500, #ced4da);
  height: 100%;

  &:hover:not(.plan-card__current-plan, .plan-card__current-plan--free-trial) {
    border: 1px solid var(--zocks-primary);

    .pricing-table__header__plan-name {
      color: var(--zocks-primary);
    }
  }
`

const EnterprisePlanRow: React.FC = () => {
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)

  const handleContactUs = tracked(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      window.location.href = `mailto:support@zocks.io?subject=${encodeURIComponent(`Upgrade to Enterprise Zocks - ${accountId}`)}`
    },
    {
      eventName: TrackEvent.PLAN_ENTERPRISE_OPENED,
    },
  )

  return (
    <Row className='mt-3'>
      <Col>
        <div className='d-flex zocks-gap-4 p-3 zocks-border-0.75 border border-1 zocks-border-color-gray-500 flex-wrap flex-lg-nowrap'>
          <div className='d-flex flex-column gap-3 flex-1'>
            <h3>Enterprise</h3>
            <div className='paragraph-medium zocks-text-gray-600'>
              Manage complex teams with advanced organizational controls and enterprise-level customization.
            </div>
            <div>
              <Button variant='primaryGhost' className='py-2 px-4' onClick={handleContactUs}>
                Contact us
              </Button>
            </div>
          </div>
          <div className='d-flex flex-column gap-1 flex-1'>
            <div className='paragraph-small bold'>Full platform experience:</div>
            <div className='d-flex gap-2'>
              <i className='bi bi-check-lg zocks-text-primary fs-6' />
              <div className='paragraph-small'>Enterprise-level insights</div>
            </div>
            <div className='d-flex gap-2'>
              <i className='bi bi-check-lg zocks-text-primary fs-6' />
              <div className='paragraph-small'>Fine -grained hierarchy management</div>
            </div>
            <div className='d-flex gap-2'>
              <i className='bi bi-check-lg zocks-text-primary fs-6' />
              <div className='paragraph-small'>Custom CRM configuration</div>
            </div>
            <div className='d-flex gap-2'>
              <i className='bi bi-check-lg zocks-text-primary fs-6' />
              <div className='paragraph-small'>Options for Tier 1 Support or Tier 1 Support enablement</div>
            </div>
            <div className='d-flex gap-2'>
              <i className='bi bi-check-lg zocks-text-primary fs-6' />
              <div className='paragraph-small'>Centralized configuration and compliance management </div>
            </div>
            <div className='d-flex gap-2'>
              <i className='bi bi-check-lg zocks-text-primary fs-6' />
              <div className='paragraph-small'>Enterprise-grade privacy, data security, and controls</div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  )
}
